import { useEffect, useRef, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { ArrowLeft, Camera, ClipboardList, Loader2, Mic, Send, Square, Volume2, VolumeX, GraduationCap, X, Lock } from "lucide-react";
import { TutorQuiz } from "./TutorQuiz";
import { TeacherAvatar } from "./TeacherAvatar";
import { firePaywall, handleFetchPaywall, errMsg } from "./paywall";

const START_TOKEN = "__START__";
const RETEACH_PREFIX = "__RETEACH__:";
const isSilent = (t) => t === START_TOKEN || t.startsWith(RETEACH_PREFIX);

export const TutorChat = ({ api, authConfig, session, onBack, studentName, language, autoplayAllowed = true }) => {
  const tamil = language === "tamil";
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [loading, setLoading] = useState(true);
  const [voiceOn, setVoiceOn] = useState(autoplayAllowed);
  const [voiceLimit, setVoiceLimit] = useState("");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [ttsAudio, setTtsAudio] = useState(null);
  const [celebrate, setCelebrate] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [err, setErr] = useState("");
  const bottomRef = useRef(null);
  const recorderRef = useRef(null);
  const audioRef = useRef(null);
  const fileRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    axios.get(`${api}/tutor/sessions/${session.session_id}/messages`, authConfig)
      .then((r) => {
        const msgs = r.data?.messages || [];
        setMessages(msgs);
        setLoading(false);
        if (msgs.length === 0 && session.fresh && !startedRef.current) {
          startedRef.current = true;
          sendMessage(START_TOKEN);
        }
      })
      .catch(() => setLoading(false));
    return () => { if (audioRef.current) audioRef.current.pause(); };
    // eslint-disable-next-line
  }, [session.session_id]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streamText]);

  const fetchOpts = () => ({
    headers: { ...(authConfig.headers || {}) },
    credentials: authConfig.withCredentials ? "include" : "same-origin",
  });

  async function readStream(resp) {
    let full = "";
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const parts = buf.split("\n\n");
      buf = parts.pop();
      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith("data: ")) continue;
        const payload = line.slice(6);
        if (payload === "[DONE]") continue;
        try {
          const obj = JSON.parse(payload);
          if (obj.t) { full += obj.t; setStreamText(full); }
          if (obj.error) throw new Error(obj.error);
        } catch (e) { if (e.message && !e.message.includes("JSON")) throw e; }
      }
    }
    return full;
  }

  function finishStream(full) {
    if (full) {
      setMessages((m) => [...m, { role: "assistant", content: full }]);
      if (voiceOn) speak(full, -1);
    }
  }

  async function sendMessage(text) {
    const clean = text.trim();
    if (!clean || streaming) return;
    setErr("");
    if (!isSilent(clean)) setMessages((m) => [...m, { role: "user", content: clean }]);
    setInput("");
    setStreaming(true); setStreamText("");
    try {
      const resp = await fetch(`${api}/tutor/sessions/${session.session_id}/message`, {
        method: "POST", ...fetchOpts(),
        headers: { "Content-Type": "application/json", ...(authConfig.headers || {}) },
        body: JSON.stringify({ text: clean }),
      });
      if (!resp.ok) {
        const pw = await handleFetchPaywall(resp);
        if (pw) throw new Error(pw.message);
        const e = await resp.json().catch(() => ({}));
        throw new Error(errMsg({ detail: e.detail }, "Teacher unavailable — try again."));
      }
      finishStream(await readStream(resp));
    } catch (e) {
      setErr(e.message || "Something went wrong.");
    } finally { setStreaming(false); setStreamText(""); }
  }

  async function sendPhoto() {
    if (!photo || streaming) return;
    const caption = input.trim();
    const file = photo;
    const localUrl = photoUrl;
    setPhoto(null); setPhotoUrl(null); setInput(""); setErr("");
    setMessages((m) => [...m, { role: "user", content: caption || "I've uploaded a photo of a problem I'm stuck on.", localUrl }]);
    setStreaming(true); setStreamText("");
    try {
      const fd = new FormData();
      fd.append("image", file, file.name || "problem.jpg");
      fd.append("caption", caption);
      const resp = await fetch(`${api}/tutor/sessions/${session.session_id}/photo`, {
        method: "POST", ...fetchOpts(), body: fd,
      });
      if (!resp.ok) {
        const pw = await handleFetchPaywall(resp);
        if (pw) throw new Error(pw.message);
        const e = await resp.json().catch(() => ({}));
        throw new Error(errMsg({ detail: e.detail }, "Photo upload failed — try again."));
      }
      finishStream(await readStream(resp));
    } catch (e) {
      setErr(e.message || "Something went wrong.");
    } finally { setStreaming(false); setStreamText(""); }
  }

  function onPickPhoto(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) { setErr("Image too large — keep it under 6 MB."); return; }
    setPhoto(f);
    setPhotoUrl(URL.createObjectURL(f));
  }

  async function speak(text, idx) {
    try {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      setSpeakingIdx(idx);
      const resp = await fetch(`${api}/tutor/tts`, {
        method: "POST", ...fetchOpts(),
        headers: { "Content-Type": "application/json", ...(authConfig.headers || {}) },
        body: JSON.stringify({ text: text.slice(0, 5900) }),
      });
      if (resp.status === 402) {
        const e = await resp.json().catch(() => ({}));
        setVoiceLimit(typeof e.detail === "object" ? e.detail.message : "Voice minutes for today are used up.");
        setSpeakingIdx(null);
        return;
      }
      if (!resp.ok) throw new Error("tts failed");
      const blob = await resp.blob();
      const audio = new Audio(URL.createObjectURL(blob));
      audioRef.current = audio;
      audio.onended = () => setSpeakingIdx(null);
      setTtsAudio(audio);
      await audio.play();
    } catch { setSpeakingIdx(null); }
  }

  function stopSpeaking() {
    if (audioRef.current) audioRef.current.pause();
    setSpeakingIdx(null);
  }

  async function toggleRecord() {
    if (recording) { recorderRef.current?.stop(); setRecording(false); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "" });
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setTranscribing(true);
        try {
          const blob = new Blob(chunks, { type: "audio/webm" });
          const fd = new FormData();
          fd.append("audio", blob, "doubt.webm");
          const r = await axios.post(`${api}/tutor/stt`, fd, authConfig);
          setInput((cur) => (cur ? cur + " " : "") + (r.data?.text || ""));
        } catch {
          setErr("Couldn't hear that clearly — try again or type it.");
        } finally { setTranscribing(false); }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch { setErr("Microphone permission needed to speak your doubt."); }
  }

  function handleSend() { photo ? sendPhoto() : sendMessage(input); }

  const avatarMood = celebrate ? "celebrating" : (streaming && !streamText) ? "thinking" : speakingIdx !== null ? "talking" : "idle";
  const avatarStatus = celebrate ? (tamil ? "உங்களைப் பற்றி பெருமை! 🎉" : "So proud of you! 🎉")
    : (streaming && !streamText) ? (tamil ? "யோசிக்கிறேன்…" : "Thinking…")
    : streaming ? (tamil ? "விளக்குகிறேன்…" : "Explaining…")
    : speakingIdx !== null ? (tamil ? "தமிழில் கற்பிக்கிறேன் 🔊" : "Teaching aloud 🔊")
    : (tamil ? "உதவ தயார்" : "Ready to help");

  const md = (content) => (
    <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>{content}</ReactMarkdown>
  );

  return (
    <div className="max-w-3xl mx-auto flex flex-col" style={{ height: "calc(100vh - 73px)" }} data-testid="tutor-chat">
      {showQuiz && (
        <TutorQuiz api={api} authConfig={authConfig} session={session}
          onClose={() => setShowQuiz(false)}
          onResult={(res) => {
            if (res.wrong_count === 0) {
              setCelebrate(true);
              if (voiceOn) speak(tamil ? "அருமை! முழு மதிப்பெண்! உங்களைப் பற்றி நான் மிகவும் பெருமைப்படுகிறேன்!" : "Fantastic! Full marks! I am so proud of you!", -1);
              setTimeout(() => setCelebrate(false), 6000);
            }
          }}
          onReteach={(quizId) => { setShowQuiz(false); sendMessage(`${RETEACH_PREFIX}${quizId}`); }}/>
      )}

      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-white/5">
        <button onClick={onBack} data-testid="chat-back-btn" className="flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm shrink-0">
          <ArrowLeft size={15}/> Back
        </button>
        <p className="text-sm text-zinc-300 font-semibold truncate" data-testid="chat-title">{session.title}</p>
        <div className="flex items-center gap-2 shrink-0">
          {session.mode === "chapter" && (
            <button onClick={() => setShowQuiz(true)} disabled={streaming} data-testid="quiz-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-purple-400/40 bg-purple-500/10 text-purple-200 hover:border-purple-300 disabled:opacity-50">
              <ClipboardList size={13}/> Quiz me
            </button>
          )}
          {!autoplayAllowed && (
            <button onClick={() => firePaywall({ message: "Auto-play teacher voice is a Plus feature. On Free, tap Listen on any reply (10 min/day)." })} data-testid="voice-autoplay-locked-btn"
              title="Auto-play voice is a Plus feature"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-zinc-700 text-zinc-500">
              <Lock size={12}/> Tap to listen
            </button>
          )}
          {autoplayAllowed && <button onClick={() => { if (voiceOn) stopSpeaking(); setVoiceOn(!voiceOn); }} data-testid="voice-toggle-btn"
            title={voiceOn ? "Teacher voice ON — click to mute" : "Teacher voice OFF — click to unmute"}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${voiceOn ? "bg-cyan-500/15 border-cyan-400 text-cyan-200" : "border-zinc-700 text-zinc-500"}`}>
            {voiceOn ? <Volume2 size={13}/> : <VolumeX size={13}/>} {voiceOn ? "Voice on" : "Voice off"}
          </button>}
        </div>
      </div>

      <div className="flex items-center gap-3 px-4 py-1.5 border-b border-white/5 bg-gradient-to-r from-cyan-500/5 to-transparent" data-testid="teacher-avatar-bar">
        <TeacherAvatar mood={avatarMood} audioObj={ttsAudio} size={68}/>
        <div>
          <p className="text-sm font-bold text-white leading-tight">Shanso Ma'am</p>
          <p className="text-[11px] text-cyan-300/80" data-testid="avatar-status">{avatarStatus}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-5 space-y-4" data-testid="chat-messages">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-cyan-400" size={22}/></div>
        ) : (
          <>
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && (
                  <div className="w-8 h-8 rounded-full bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center mr-2 shrink-0 mt-1">
                    <GraduationCap size={14} className="text-cyan-300"/>
                  </div>
                )}
                <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user"
                  ? "bg-cyan-500/15 border border-cyan-400/25 text-white"
                  : "bg-[#111111] border border-zinc-800 text-zinc-100"}`}
                  data-testid={`msg-${m.role}-${i}`}>
                  {(m.photo_id || m.localUrl) && <PhotoImg api={api} authConfig={authConfig} msg={m}/>}
                  <div className="prose prose-invert prose-sm max-w-none [&_p]:my-1.5 [&_ul]:my-1.5 [&_ol]:my-1.5 [&_strong]:text-cyan-200 [&_.katex]:text-[1.05em]">
                    {md(m.content)}
                  </div>
                  {m.role === "assistant" && (
                    <button onClick={() => speakingIdx === i ? stopSpeaking() : speak(m.content, i)} data-testid={`speak-msg-${i}`}
                      className="mt-2 text-[11px] text-zinc-500 hover:text-cyan-300 flex items-center gap-1">
                      {speakingIdx === i ? <><Square size={10}/> Stop</> : <><Volume2 size={11}/> Listen</>}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {streaming && (
              <div className="flex justify-start">
                <div className="w-8 h-8 rounded-full bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center mr-2 shrink-0 mt-1">
                  <GraduationCap size={14} className="text-cyan-300"/>
                </div>
                <div className="max-w-[85%] rounded-2xl px-4 py-3 text-sm bg-[#111111] border border-zinc-800 text-zinc-100" data-testid="streaming-bubble">
                  {streamText ? (
                    <div className="prose prose-invert prose-sm max-w-none [&_p]:my-1.5 [&_strong]:text-cyan-200">{md(streamText)}</div>
                  ) : (
                    <span className="text-zinc-500 text-xs flex items-center gap-2"><Loader2 size={12} className="animate-spin"/> Teacher is thinking…</span>
                  )}
                </div>
              </div>
            )}
          </>
        )}
        <div ref={bottomRef}/>
      </div>

      {err && <p className="px-4 pb-1 text-red-300 text-xs" data-testid="chat-error">⚠ {err}</p>}
      {voiceLimit && (
        <div className="mx-4 mb-2 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-400/30 text-amber-200 text-xs flex items-center justify-between gap-3" data-testid="voice-limit-banner">
          <span className="flex items-center gap-1.5"><Lock size={11}/> {voiceLimit}</span>
          <span className="flex items-center gap-2 shrink-0">
            <button onClick={() => firePaywall({ message: voiceLimit })} data-testid="voice-limit-upgrade-btn" className="font-bold underline underline-offset-2">Upgrade</button>
            <button onClick={() => setVoiceLimit("")} className="text-amber-300/70 hover:text-white"><X size={12}/></button>
          </span>
        </div>
      )}

      {photoUrl && (
        <div className="px-4 pt-2 flex items-center gap-3" data-testid="photo-preview">
          <img src={photoUrl} alt="preview" className="h-16 rounded-lg border border-cyan-400/40"/>
          <p className="text-xs text-zinc-400 flex-1">Photo ready — add a caption (optional) and press send.</p>
          <button onClick={() => { setPhoto(null); setPhotoUrl(null); }} data-testid="photo-cancel-btn" className="text-zinc-500 hover:text-red-400"><X size={16}/></button>
        </div>
      )}

      <div className="px-4 pb-4 pt-2 border-t border-white/5">
        <div className="flex items-end gap-2">
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPickPhoto} className="hidden" data-testid="photo-input"/>
          <button onClick={() => fileRef.current?.click()} disabled={streaming} data-testid="camera-btn"
            title="Snap a photo of your problem"
            className="p-3 rounded-full border border-zinc-700 text-zinc-400 hover:border-cyan-400 hover:text-cyan-300 transition-colors shrink-0 disabled:opacity-50">
            <Camera size={17}/>
          </button>
          <button onClick={toggleRecord} disabled={transcribing || streaming} data-testid="mic-btn"
            title={recording ? "Stop recording" : "Speak your doubt"}
            className={`p-3 rounded-full border transition-colors shrink-0 ${recording
              ? "bg-red-500/20 border-red-400 text-red-300 animate-pulse"
              : "border-zinc-700 text-zinc-400 hover:border-cyan-400 hover:text-cyan-300"}`}>
            {transcribing ? <Loader2 size={17} className="animate-spin"/> : recording ? <Square size={17}/> : <Mic size={17}/>}
          </button>
          <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder={recording ? (tamil ? "கேட்கிறேன்… முடிந்ததும் சதுரத்தை தட்டுங்கள்" : "Listening… tap the square to finish") : photo ? "Add a caption for your photo…" : tamil ? `தமிழில் பேசுங்கள் அல்லது தட்டச்சு செய்யுங்கள், ${studentName?.split(" ")[0] || "மாணவர்"}…` : `Type or speak your question, ${studentName?.split(" ")[0] || "student"}…`}
            data-testid="chat-input"
            className="flex-1 px-4 py-3 bg-[#0A0A0A] border border-zinc-800 rounded-2xl text-sm text-white focus:border-cyan-400 focus:outline-none resize-none"/>
          <button onClick={handleSend} disabled={streaming || (!input.trim() && !photo)} data-testid="send-btn"
            className="p-3 rounded-full font-bold text-black disabled:opacity-40 shrink-0"
            style={{ background: "linear-gradient(135deg, #00F0FF, #22D3EE)" }}>
            <Send size={17}/>
          </button>
        </div>
      </div>
    </div>
  );
};

function PhotoImg({ api, authConfig, msg }) {
  const [url, setUrl] = useState(msg.localUrl || null);
  useEffect(() => {
    if (url || !msg.photo_id) return;
    fetch(`${api}/tutor/photos/${msg.photo_id}`, {
      headers: { ...(authConfig.headers || {}) },
      credentials: authConfig.withCredentials ? "include" : "same-origin",
    }).then((r) => (r.ok ? r.blob() : null)).then((b) => b && setUrl(URL.createObjectURL(b))).catch(() => {});
    // eslint-disable-next-line
  }, [msg.photo_id]);
  if (!url) return null;
  return <img src={url} alt="problem" className="rounded-lg max-h-48 mb-2 border border-zinc-700" data-testid="photo-attachment"/>;
}
